import * as M from "../Model/viewingsModel"
import { cmd } from "elm-ts"
import { Variant, IO, AgentViewing } from "@flypengine/myFlyp-lib"
import { flow, identity, pipe } from "fp-ts/function"
import * as t from "io-ts"
import * as tx from "io-ts-types"
import * as O from "fp-ts/Option"
import * as A from "fp-ts/Array"
import * as R from "fp-ts/Record"
import dayjs from "dayjs"
import * as E from "fp-ts/Either"
import { sequenceS } from "fp-ts/lib/Apply"
import { API_BASE_URL } from "../../runtimeVariables"
import * as http from "elm-ts/lib/Http"
import * as Http from "../../Http"
import { monthlyCalendar } from "../../lib/calendar"
import { Types } from "@flypengine/lib-shared"

export const initModel: M.Model = M.viewingsInitModel
export const initCmd: cmd.Cmd<M.Msg> = cmd.of(M.Msg.init())

const calendarOverrideInit: Types.CalendarOverride = pipe(
  [
    {
      dates: [dayjs().format("YYYY-MM-DD"), dayjs().format("YYYY-MM-DD")],
      times: [],
    },
  ],
  Types.CalendarOverride.decode,
  O.fromEither,
  O.flatten
)

export const update: <msg>(
  config: M.Config<msg>
) => (msg: M.Msg) => (model: M.Model) => [M.Model, cmd.Cmd<msg>] =
  config => msg => model =>
    pipe(
      msg,
      Variant.caseOf({
        [M._init]: () => [
          { ...model, calendar: O.some(monthlyCalendar(new Date())) },
          pipe(config.wrapperMsg, config.dealViewingsMsg, cmd.of),
        ],
        [M._viewingsCalendarReq]: () => [
          {
            ...model,
            calendarModel: pipe(
              model.calendarModel,
              E.map(prevContents => ({
                ...prevContents,
                entries: [],
              }))
            ),
          },
          pipe(
            model.calendar,
            O.match(
              () => cmd.none,
              calendar =>
                pipe(
                  {
                    from: A.head(calendar.entries),
                    to: A.last(calendar.entries),
                  },
                  sequenceS(O.Apply),
                  O.match(
                    () => cmd.none,
                    ({ from, to }) =>
                      pipe(
                        http.get(
                          `${API_BASE_URL}/myFlyp/viewings-calendar/${encodeURI(
                            config.propcode
                          )}?start=${dayjs(from.date).format(
                            "YYYY-MM-DD"
                          )}&end=${dayjs(to.date).format("YYYY-MM-DD")}`,
                          flow(
                            t.record(t.string, t.array(AgentViewing)).decode,
                            E.mapLeft(IO.validationErrorsToString)
                          )
                        ),
                        Http.sendRequest(config.user)(
                          flow(
                            Http.resultFromResponse,
                            M.Msg.viewingsCalendarRes,
                            config.wrapperMsg
                          )
                        )
                      )
                  )
                )
            )
          ),
        ],
        [M._viewingsCalendarRes]: response =>
          pipe(
            response,
            E.match(
              () => [model, cmd.none],
              viewings => [
                {
                  ...model,
                  calendarModel: pipe(
                    model.calendarModel,
                    E.map(prevContents => ({
                      ...prevContents,
                      entries: pipe(Object.values(viewings), A.flatten),
                    }))
                  ),
                },
                cmd.none,
              ]
            )
          ),
        [M._calendarContents]: newCalendar => [
          { ...model, calendar: O.some(newCalendar) },
          pipe(M.Msg.viewingsCalendarReq(), config.wrapperMsg, cmd.of),
        ],
        [M._calendarDayModel]: newCalendarDayModel =>
          pipe(
            newCalendarDayModel,
            O.match(
              () => [
                {
                  ...model,
                  calendarModel: pipe(
                    model.calendarModel,
                    E.map(prevContents => ({
                      ...prevContents,
                      selectedDay: O.none,
                    }))
                  ),
                },
                cmd.none,
              ],
              selection => [
                {
                  ...model,
                  calendarModel: pipe(
                    model.calendarModel,
                    E.map(prevContents => ({
                      ...prevContents,
                      selectedDay: O.some({
                        date: selection,
                        entries: pipe(
                          prevContents.entries,
                          A.filterMap(entry =>
                            dayjs(entry.viewingDate).format("YYYY-MM-DD") ===
                            dayjs(selection).format("YYYY-MM-DD")
                              ? O.some(entry)
                              : O.none
                          )
                        ),
                      }),
                    }))
                  ),
                },
                cmd.none,
              ]
            )
          ),
        [M._toggleCalendarSetup]: () => {
          // If closing the modal (currently open), reset calendar override to saved value
          const isClosing = model.isCalendarSetupOpen
          return [
            {
              ...model,
              isCalendarSetupOpen: !model.isCalendarSetupOpen,
              calendarSetup: isClosing
                ? {
                    ...model.calendarSetup,
                    calendarOverride: model.calendarSetup.savedCalendarOverride,
                  }
                : model.calendarSetup,
            },
            cmd.none,
          ]
        },
        [M._onChangeCalendarAvailability]: ({ setField, idx }) => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              newCalendarAvailability: pipe(
                setField,
                R.mapWithIndex((key, value) =>
                  pipe(
                    model.calendarSetup.newCalendarAvailability,
                    O.chain(newCalendarAvailability =>
                      pipe(
                        newCalendarAvailability,
                        R.lookup(key),
                        O.flatten,
                        O.chain(timeSlots =>
                          pipe(
                            value,
                            O.chain(ts => pipe(timeSlots, A.updateAt(idx, ts)))
                          )
                        )
                      )
                    ),
                    O.alt(() =>
                      pipe(setField, R.lookup(key), O.flatten, O.map(A.of))
                    )
                  )
                ),
                dayAvailability =>
                  pipe(
                    model.calendarSetup.newCalendarAvailability,
                    O.map(calendarAvailability => ({
                      ...calendarAvailability,
                      ...dayAvailability,
                    }))
                  )
              ),
              // Clear any error when changing availability
              submitAvailability: E.left(Http.ReqStatus.notAttempted()),
            },
          },
          cmd.none,
        ],
        [M._addAvailabilityTimeSlot]: day => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              newCalendarAvailability: pipe(
                model.calendarSetup.newCalendarAvailability,
                O.map(availability => ({
                  ...availability,
                  [day]: pipe(
                    availability[day],
                    O.map(A.concat(Types.defaultTimeSlots)),
                    O.alt(() => O.some(Types.defaultTimeSlots))
                  ),
                }))
              ),
              // Clear any error when adding a time slot
              submitAvailability: E.left(Http.ReqStatus.notAttempted()),
            },
          },
          cmd.none,
        ],
        [M._removeAvailabilityTimeSlot]: day => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              newCalendarAvailability: pipe(
                model.calendarSetup.newCalendarAvailability,
                O.map(availability => ({
                  ...availability,
                  [day]: pipe(
                    availability[day],
                    O.chain(timeSlots =>
                      pipe(
                        timeSlots,
                        A.deleteAt(timeSlots.length - 1),
                        O.chain(O.fromPredicate(arr => !A.isEmpty(arr)))
                      )
                    )
                  ),
                }))
              ),
              // Clear any error when removing a time slot
              submitAvailability: E.left(Http.ReqStatus.notAttempted()),
            },
          },
          cmd.none,
        ],
        [M._onChangeMinimumNotice]: notice => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              calendarNoticePeriodHours: O.some(notice),
            },
          },
          cmd.none,
        ],
        [M._updateCalendarSettingsReq]: () =>
          pipe(
            model.calendarSetup.newCalendarAvailability,
            O.fold(
              () => [
                {
                  ...model,
                  calendarSetup: {
                    ...model.calendarSetup,
                    submitAvailability: E.left(
                      Http.ReqStatus.error("No calendar availability")
                    ),
                  },
                },
                cmd.none,
              ],
              calendarAvailability => {
                // Extract viewing dates from calendarModel
                const viewingDates = pipe(
                  model.calendarModel,
                  E.match(
                    () => [],
                    calendarData =>
                      pipe(
                        calendarData.entries,
                        A.map(viewing => viewing.viewingDate)
                      )
                  )
                )

                return pipe(
                  Types.validateCalendar({
                    calendarAvailability,
                    calendarOverride: model.calendarSetup.calendarOverride,
                    calendarNoticePeriodHours:
                      model.calendarSetup.calendarNoticePeriodHours,
                    viewingDates,
                    oldCalendarAvailability:
                      model.calendarSetup.calendarAvailability,
                  }),
                  O.fold(
                    () => [
                      {
                        ...model,
                        calendarSetup: {
                          ...model.calendarSetup,
                          submitAvailability: E.left(Http.ReqStatus.loading()),
                        },
                      },
                      pipe(
                        Http.put(
                          `${API_BASE_URL}/myFlyp/calendar/${encodeURI(
                            config.propcode
                          )}`,
                          pipe(
                            {
                              calendar_availability: calendarAvailability,
                              calendar_override:
                                model.calendarSetup.calendarOverride,
                              calendar_notice_period_hours:
                                model.calendarSetup.calendarNoticePeriodHours,
                            },
                            t.type({
                              calendar_availability: Types.CalendarAvailability,
                              calendar_override: Types.CalendarOverride,
                              calendar_notice_period_hours:
                                tx.optionFromNullable(t.number),
                            }).encode
                          ),
                          flow(
                            t.type({}).decode,
                            E.mapLeft(IO.validationErrorsToString)
                          )
                        ),
                        Http.sendRequest(config.user)(
                          flow(
                            Http.resultFromResponse,
                            M.Msg.updateCalendarSettingsRes,
                            config.wrapperMsg
                          )
                        )
                      ),
                    ],
                    error => [
                      {
                        ...model,
                        calendarSetup: {
                          ...model.calendarSetup,
                          submitAvailability: E.left(
                            Http.ReqStatus.error(error)
                          ),
                        },
                      },
                      cmd.none,
                    ]
                  )
                )
              }
            )
          ),
        [M._updateCalendarSettingsRes]: result => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              submitAvailability: result,
              // Update calendarAvailability to match newCalendarAvailability on success
              calendarAvailability: pipe(
                result,
                E.fold(
                  () => model.calendarSetup.calendarAvailability,
                  () => model.calendarSetup.newCalendarAvailability
                )
              ),
              // Update saved override on success
              savedCalendarOverride: pipe(
                result,
                E.fold(
                  () => model.calendarSetup.savedCalendarOverride,
                  () => model.calendarSetup.calendarOverride
                )
              ),
            },
            // Close modal on success
            isCalendarSetupOpen: pipe(
              result,
              E.fold(
                () => model.isCalendarSetupOpen,
                () => false
              )
            ),
          },
          cmd.none,
        ],
        [M._openCancellationModal]: viewing => [
          {
            ...model,
            cancellationRequest: O.some({
              viewing,
              reason: "",
              submitStatus: E.left(Http.ReqStatus.notAttempted()),
            }),
          },
          cmd.none,
        ],
        [M._closeCancellationModal]: () => [
          { ...model, cancellationRequest: O.none },
          cmd.none,
        ],
        [M._changeCancellationReason]: reason => [
          {
            ...model,
            cancellationRequest: pipe(
              model.cancellationRequest,
              O.map(req => ({ ...req, reason }))
            ),
          },
          cmd.none,
        ],
        [M._submitCancellationReq]: () => [
          {
            ...model,
            cancellationRequest: pipe(
              model.cancellationRequest,
              O.map(req => ({
                ...req,
                submitStatus: E.left(Http.ReqStatus.loading()),
              }))
            ),
          },
          pipe(
            model.cancellationRequest,
            O.match(
              () => cmd.none,
              req =>
                pipe(
                  http.post(
                    `${API_BASE_URL}/myFlyp/viewings/${encodeURI(
                      config.propcode
                    )}/cancel`,
                    {
                      viewing_id: req.viewing.viewingId,
                      viewing_date: req.viewing.viewingDate.toISOString(),
                      agent_brand: req.viewing.agent.brand,
                      agent_branch: pipe(
                        req.viewing.agent.branch,
                        O.toNullable
                      ),
                      cancellation_reason: req.reason,
                    },
                    flow(
                      t.type({}).decode,
                      E.mapLeft(IO.validationErrorsToString)
                    )
                  ),
                  Http.sendRequest(config.user)(
                    flow(
                      Http.resultFromResponse,
                      M.Msg.submitCancellationRes,
                      config.wrapperMsg
                    )
                  )
                )
            )
          ),
        ],
        [M._submitCancellationRes]: result => [
          {
            ...model,
            cancellationRequest: pipe(
              result,
              E.fold(
                () => model.cancellationRequest, // Keep modal open on error
                () => O.none // Close modal on success
              )
            ),
          },
          cmd.none,
        ],
        [M._onChangeAvailabilityOverrideDates]: ({ blockedDates, idx }) => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              calendarOverride: pipe(
                model.calendarSetup.calendarOverride,
                O.alt(() => calendarOverrideInit),
                O.map(
                  A.mapWithIndex((index, datesTimes) =>
                    index === idx
                      ? pipe(
                          blockedDates,
                          O.fold(
                            () => datesTimes,
                            newDates => ({
                              dates: newDates,
                              times: datesTimes.times,
                            })
                          )
                        )
                      : datesTimes
                  )
                )
              ),
            },
          },
          cmd.none,
        ],
        [M._onChangeAvailabilityOverrideTimeSlot]: ({
          timeSlot,
          outerIdx,
          innerIdx,
        }) => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              calendarOverride: pipe(
                timeSlot,
                O.fold(
                  () =>
                    pipe(
                      model.calendarSetup.calendarOverride,
                      O.alt(() => calendarOverrideInit),
                      O.map(
                        A.mapWithIndex((idx, datesTimes) =>
                          idx === outerIdx
                            ? {
                                ...datesTimes,
                                times: A.unsafeDeleteAt(
                                  innerIdx,
                                  datesTimes.times
                                ),
                              }
                            : datesTimes
                        )
                      )
                    ),
                  timeSlot =>
                    pipe(
                      model.calendarSetup.calendarOverride,
                      O.alt(() => calendarOverrideInit),
                      O.map(
                        A.mapWithIndex((index, datesTimes) =>
                          pipe(
                            index === outerIdx
                              ? pipe(
                                  datesTimes.times,
                                  A.mapWithIndex((idx, ts) =>
                                    idx === innerIdx ? timeSlot : ts
                                  ),
                                  times => ({ dates: datesTimes.dates, times })
                                )
                              : datesTimes
                          )
                        )
                      )
                    )
                )
              ),
            },
          },
          cmd.none,
        ],
        [M._onAddAvailabilityOverrideTimeSlot]: ({ idx, timeSlot }) => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              calendarOverride: pipe(
                model.calendarSetup.calendarOverride,
                O.alt(() => calendarOverrideInit),
                O.map(
                  A.mapWithIndex((index, datesTimes) =>
                    index === idx
                      ? {
                          dates: datesTimes.dates,
                          times: pipe(
                            timeSlot,
                            O.fold(
                              () =>
                                pipe(
                                  datesTimes.times,
                                  A.append({ start: "", end: "" })
                                ),
                              timeSlot =>
                                pipe(datesTimes.times, A.append(timeSlot))
                            )
                          ),
                        }
                      : datesTimes
                  )
                )
              ),
            },
          },
          cmd.none,
        ],
        [M._onAddAvailabilityOverride]: () => {
          const newDateBlocker: {
            dates: [string, string]
            times: { start: string; end: string }[]
          } = pipe(
            calendarOverrideInit,
            O.chain(A.head),
            O.fold(
              () => ({
                dates: [
                  dayjs().format("YYYY-MM-DD"),
                  dayjs().format("YYYY-MM-DD"),
                ],
                times: [],
              }),
              identity
            )
          )

          return [
            {
              ...model,
              calendarSetup: {
                ...model.calendarSetup,
                calendarOverride: pipe(
                  model.calendarSetup.calendarOverride,
                  O.map(A.append(newDateBlocker)),
                  O.alt(() => calendarOverrideInit)
                ),
              },
            },
            cmd.none,
          ]
        },
        [M._onRemoveAvailabilityOverride]: idx => [
          {
            ...model,
            calendarSetup: {
              ...model.calendarSetup,
              calendarOverride: pipe(
                model.calendarSetup.calendarOverride,
                O.chain(A.deleteAt(idx)),
                O.chain(O.fromPredicate(arr => !A.isEmpty(arr)))
              ),
            },
          },
          cmd.none,
        ],
      })
    )
